import Camera from 'lucide-react/dist/esm/icons/camera';
import CloudUpload from 'lucide-react/dist/esm/icons/cloud-upload';
import FileImage from 'lucide-react/dist/esm/icons/file-image';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2';
import Upload from 'lucide-react/dist/esm/icons/upload';
import X from 'lucide-react/dist/esm/icons/x';
import { Label } from '@/components/ui/label';
import { getReviewImageUrl } from '@/lib/image-urls';
import type { ProgressMessage, ReviewMedia } from '@/lib/reviews';

interface ImageUploadProps {
  uploadedImages: ReviewMedia[];
  uploadingFiles: Map<string, File>;
  previewUrls: Map<string, string>;
  isDragging: boolean;
  progressMessages: ProgressMessage[];
  errors: string;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
  onDeleteUploadedImage: (mediaId: string) => void;
  onRemoveUploadingFile: (fileId: string) => void;
  onRemoveProgressMessage: (id: string) => void;
}

const MAX_IMAGES = 10;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / k ** i).toFixed(1)) + ' ' + sizes[i];
}

export default function ImageUpload({
  uploadedImages,
  uploadingFiles,
  previewUrls,
  isDragging,
  progressMessages,
  errors,
  onFileSelect,
  onDragOver,
  onDragLeave,
  onDrop,
  onDeleteUploadedImage,
  onRemoveUploadingFile,
  onRemoveProgressMessage,
}: ImageUploadProps) {
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    showFallback: () => void,
  ) => {
    const target = e.currentTarget;
    target.style.display = 'none';
    showFallback();
  };

  return (
    <div className="space-y-3 text-right">
      {/* Header with counter */}
      <div className="flex flex-row-reverse items-center justify-between">
        <Label>תמונות (אופציונלי)</Label>
        <span className="text-xs text-gray-500">
          {uploadedImages.length + uploadingFiles.size}/{MAX_IMAGES}
        </span>
      </div>

      {/* Progress Messages - Fixed space, show only latest */}
      <div className="min-h-[40px]">
        {progressMessages.length > 0 &&
          (() => {
            const message = progressMessages[progressMessages.length - 1];
            return (
              <div
                key={message.id}
                className={`flex flex-row-reverse items-center justify-between rounded-md border p-2 text-xs transition-all duration-300
                ${
                  message.type === 'success'
                    ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20'
                    : ''
                }
                ${
                  message.type === 'error'
                    ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20'
                    : ''
                }
                ${
                  message.type === 'info'
                    ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20'
                    : ''
                }`}
              >
                <div className="flex flex-row-reverse items-center gap-1.5">
                  {message.type === 'success' && (
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500"></div>
                  )}
                  {message.type === 'error' && (
                    <div className="h-1.5 w-1.5 rounded-full bg-red-500"></div>
                  )}
                  {message.type === 'info' && (
                    <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></div>
                  )}

                  <span
                    className={`font-medium
                    ${message.type === 'success' ? 'text-green-800 dark:text-green-200' : ''}
                    ${message.type === 'error' ? 'text-red-800 dark:text-red-200' : ''}
                    ${message.type === 'info' ? 'text-blue-800 dark:text-blue-200' : ''}`}
                  >
                    {message.text}
                  </span>
                </div>

                {message.type === 'error' && (
                  <button
                    type="button"
                    onClick={() => onRemoveProgressMessage(message.id)}
                    className="text-red-600 transition-colors hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          })()}
      </div>

      {/* Compact Add Images Button */}
      {uploadedImages.length + uploadingFiles.size < MAX_IMAGES && (
        <div
          className={`relative rounded-md border-2 border-dashed transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
              : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
          } ${errors ? 'border-red-500' : ''}`}
        >
          <input
            type="file"
            id="images"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={onFileSelect}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <div className="flex items-center justify-center gap-2 p-3 text-sm">
            {uploadingFiles.size > 0 ? (
              <>
                <Upload className="h-4 w-4 animate-pulse text-blue-500" />
                <span className="font-medium text-blue-600 dark:text-blue-400">
                  מעלה {uploadingFiles.size} תמונות...
                </span>
              </>
            ) : isDragging ? (
              <>
                <CloudUpload className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-blue-600 dark:text-blue-400">שחררו כאן</span>
              </>
            ) : (
              <>
                <Camera className="h-4 w-4 text-gray-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  הוסיפו תמונות (גררו או לחצו)
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* File Preview Grid */}
      {(uploadedImages.length > 0 || uploadingFiles.size > 0) && (
        <div className="space-y-3 text-right">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            תמונות ({uploadedImages.length + uploadingFiles.size}/{MAX_IMAGES})
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {/* Uploaded Images */}
            {uploadedImages.map((media) => (
              <div
                key={media.id}
                className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
              >
                <img
                  src={getReviewImageUrl(media.s3Key)}
                  alt={media.filename}
                  className="h-24 w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => onDeleteUploadedImage(media.id)}
                  className="absolute left-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 shadow-lg transition-all hover:bg-red-600 group-hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="p-2 text-right">
                  <p className="truncate text-xs text-gray-600 dark:text-gray-400">
                    {media.filename}
                  </p>
                  <div className="mt-1 flex flex-row-reverse items-center gap-1">
                    <div className="h-2 w-2 rounded-full bg-green-500"></div>
                    <span className="text-xs text-green-600 dark:text-green-400">הועלה</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Uploading Files */}
            {Array.from(uploadingFiles.entries()).map(([fileId, file]) => {
              let fallbackVisible = false;
              const setFallbackVisible = () => {
                fallbackVisible = true;
              };

              return (
                <div
                  key={fileId}
                  className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <img
                    src={previewUrls.get(fileId) || ''}
                    alt={file.name}
                    className="h-24 w-full bg-gray-100 object-cover dark:bg-gray-700"
                    onError={(e) => handleImageError(e, setFallbackVisible)}
                    style={{ display: fallbackVisible ? 'none' : 'block' }}
                  />
                  {/* Fallback for failed images */}
                  <div
                    className="flex h-24 w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-gray-700"
                    style={{ display: fallbackVisible ? 'flex' : 'none' }}
                  >
                    <FileImage className="h-8 w-8" />
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveUploadingFile(fileId)}
                    className="absolute left-1 top-1 rounded-full bg-red-500 p-1 text-white opacity-0 shadow-lg transition-all hover:bg-red-600 group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div className="p-2 text-right">
                    <p className="truncate text-xs text-gray-600 dark:text-gray-400">{file.name}</p>
                    <div className="mt-1 flex flex-row-reverse items-center justify-between">
                      <div className="flex flex-row-reverse items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        <span className="text-xs text-blue-600 dark:text-blue-400">מעלה</span>
                      </div>
                      <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {errors && (
        <p className="rounded border border-red-200 bg-red-50 p-2 text-right text-sm text-red-500 dark:border-red-800 dark:bg-red-950/20">
          {errors}
        </p>
      )}
    </div>
  );
}
